import { cva } from "class-variance-authority";
import * as React from "react";
import { Link as ReactLink } from "react-router-dom";

import type { MenuItemProps } from "./types";

import { cn } from "../../../lib/utils";

import { Icon } from "../Icon";

const ICON_SIZE = 20;

const menuItemVariants = cva(
	[
		"group/menu-item relative flex items-center gap-3 transition-colors",
		"box-border shrink-0",
		"m-0 appearance-none border-0 bg-transparent no-underline [font-family:inherit]",
		"text-[color:var(--sidenav-fg,currentColor)]",
		"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--sidenav-fg,currentColor)]",
	],
	{
		variants: {
			variant: {
				link: "mx-2 h-10 rounded-md px-2 text-paragraph-3 font-semibold",
				"nested-link": "ml-10 mr-2 h-10 rounded-md px-2 text-paragraph-3 font-normal",
				category: [
					"mt-3 flex h-6 items-end px-4",
					"text-caption font-normal",
					"text-[color:var(--sidenav-fg-muted,currentColor)]",
					"pointer-events-none cursor-default opacity-70",
				],
			},
			isActive: { true: "", false: "" },
		},
		compoundVariants: [
			{
				variant: "link",
				isActive: false,
				className: "cursor-pointer hover:bg-[color:var(--sidenav-hover-bg,transparent)]",
			},
			{
				variant: "nested-link",
				isActive: false,
				className: "cursor-pointer hover:bg-[color:var(--sidenav-hover-bg,transparent)]",
			},
			{
				variant: "link",
				isActive: true,
				className: "bg-[color:var(--sidenav-active-bg,transparent)]",
			},
			{
				variant: "nested-link",
				isActive: true,
				className: "bg-[color:var(--sidenav-active-bg,transparent)]",
			},
		],
		defaultVariants: {
			variant: "link",
			isActive: false,
		},
	}
);

export const MenuItem = React.forwardRef<HTMLAnchorElement | HTMLButtonElement, MenuItemProps>((props, ref) => {
	const {
		variant = "link",
		label,
		href,
		iconLeft,
		isActive = false,
		onClick,
		fullPageLoad = false,
		isExternal = false,
		dangerouslySetClassName,
		"data-testid": dataTestId,
	} = props;

	const isCategory = variant === "category";
	const showIconColumn = variant === "link";
	const className = cn(menuItemVariants({ variant, isActive }), dangerouslySetClassName);

	const content = (
		<>
			{showIconColumn ? (
				<span aria-hidden className="flex size-5 shrink-0 items-center justify-center">
					{iconLeft ? <Icon icon={iconLeft} size={ICON_SIZE} color={null} /> : null}
				</span>
			) : null}
			<span className="min-w-0 flex-1 truncate text-left">{label}</span>
		</>
	);

	if (isCategory) {
		return (
			<div className={className} role="presentation" data-testid={dataTestId}>
				<span className="min-w-0 flex-1 truncate text-left">{label}</span>
			</div>
		);
	}

	if (href) {
		if (isExternal || fullPageLoad) {
			return (
				<a
					ref={ref as React.Ref<HTMLAnchorElement>}
					href={href}
					className={className}
					onClick={onClick}
					target={isExternal ? "_blank" : undefined}
					rel={isExternal ? "noopener noreferrer" : undefined}
					aria-current={isActive ? "page" : undefined}
					data-testid={dataTestId}
				>
					{content}
				</a>
			);
		}
		return (
			<ReactLink
				ref={ref as React.Ref<HTMLAnchorElement>}
				to={href}
				className={className}
				onClick={onClick}
				aria-current={isActive ? "page" : undefined}
				data-testid={dataTestId}
			>
				{content}
			</ReactLink>
		);
	}

	return (
		<button
			ref={ref as React.Ref<HTMLButtonElement>}
			type="button"
			className={className}
			onClick={onClick}
			aria-current={isActive ? "page" : undefined}
			data-testid={dataTestId}
		>
			{content}
		</button>
	);
});

MenuItem.displayName = "MenuItem";

export { menuItemVariants };
export type { MenuItemProps, MenuItemVariant } from "./types";
